const TARGETED_TOOLS = new Set(["run_node", "inspect_node_output", "update_node"]);

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

const CASCADE_PREFIX = "all-from-";

function nodeId(value: unknown): string | undefined {
  if (typeof value !== "string" || !value) return undefined;
  // Cascade probes report a synthetic `all-from-<id>` target; the run starts at <id>.
  const id = value.startsWith(CASCADE_PREFIX) ? value.slice(CASCADE_PREFIX.length) : value;
  return id || undefined;
}

/**
 * Returns a node id to focus, null to clear focus, or undefined when the frame
 * does not change node activity.
 */
export function nodeActivityFromFrame(event: string, data: unknown): string | null | undefined {
  const payload = record(data);

  // Local and BFF execution routes wrap step events in an `execution` frame.
  if (event === "execution" && typeof payload.type === "string") {
    return nodeActivityFromFrame(payload.type, payload);
  }

  if (event === "step-start") {
    return nodeId(payload.nodeId) ?? nodeId(payload.stepId);
  }

  if (event === "run-progress" && payload.status === "running") {
    return nodeId(payload.nodeId) ?? nodeId(payload.untilNodeId);
  }

  // The builder adds and patches nodes one at a time, so the node carried by a
  // graph mutation is the step the agent is working on right now.
  if (event === "node-added" || event === "node-updated") {
    return nodeId(record(payload.node).id);
  }

  if (event === "tool-call") {
    const tool = String(payload.tool ?? "");
    if (!TARGETED_TOOLS.has(tool)) return undefined;
    const args = record(payload.args);
    return nodeId(args.nodeId) ?? nodeId(args.id);
  }

  if (
    event === "run-result" ||
    event === "complete" ||
    event === "result" ||
    event === "workflow-complete" ||
    event === "workflow-error" ||
    event === "agent-stopped"
  ) {
    return null;
  }

  return undefined;
}

/**
 * Same as `nodeActivityFromFrame`, but drops ids the canvas cannot render yet.
 * Graph mutations are trusted because they carry the node being added in the
 * very same frame, so `nodeExists` has not seen it yet.
 */
export function nodeHighlightFromFrame(
  event: string,
  data: unknown,
  nodeExists: (id: string) => boolean,
): string | null | undefined {
  const activeNode = nodeActivityFromFrame(event, data);
  if (activeNode === null || activeNode === undefined) return activeNode;
  if (event === "node-added" || event === "node-updated") return activeNode;
  return nodeExists(activeNode) ? activeNode : undefined;
}
